/* ONLI.Shop Theme — global.js
   Progressive-enhancement web components: everything renders server-side first; these add the drawers,
   AJAX cart, header behaviour and small interactions on top. */

(() => {
  'use strict';

  /* ---------- Utilities ---------- */
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  });

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const FOCUSABLE =
    'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe';

  const getFocusable = (container) => Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);

  const trapFocus = {
    container: null,
    handlers: {},
    activate(container, elementToFocus = container) {
      this.deactivate();
      this.container = container;
      // boundaries are looked up per Tab press, so the trap survives re-rendered contents
      this.handlers.keydown = (event) => {
        if (event.code !== 'Tab' || !this.container) return;
        const focusables = getFocusable(this.container);
        if (!focusables.length) {
          event.preventDefault();
          this.container.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        const inside = this.container.contains(active) && active !== this.container;
        if (!event.shiftKey && (!inside || active === last)) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && (!inside || active === first)) {
          event.preventDefault();
          last.focus();
        }
      };
      document.addEventListener('keydown', this.handlers.keydown);
      this.focus(elementToFocus);
    },
    focus(element) {
      if (!element) return;
      element.focus({ preventScroll: true });
      if (element.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(element.type) && element.value) {
        element.setSelectionRange(0, element.value.length);
      }
    },
    deactivate() {
      document.removeEventListener('keydown', this.handlers.keydown);
      this.container = null;
    },
  };

  // created up front: screen readers skip the first message in a freshly inserted live region
  const liveRegion = (() => {
    let region = document.getElementById('ThemeLiveRegion');
    if (!region) {
      region = document.createElement('div');
      region.id = 'ThemeLiveRegion';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    return region;
  })();

  const announce = (message) => {
    liveRegion.textContent = '';
    requestAnimationFrame(() => (liveRegion.textContent = message));
  };

  window.theme = window.theme || {};
  window.theme.utils = { debounce, fetchConfig, parseHTML, announce };

  /* ---------- Shared overlay ---------- */
  const Overlay = {
    el: null,
    get() {
      if (!this.el) {
        this.el = document.createElement('div');
        this.el.className = 'drawer__overlay';
        this.el.addEventListener('click', () => DrawerDialog.closeAll());
        document.body.appendChild(this.el);
      }
      return this.el;
    },
    show() {
      this.get().classList.add('is-open');
    },
    hide() {
      if (this.el) this.el.classList.remove('is-open');
    },
  };

  /* ---------- Drawer dialog ---------- */
  class DrawerDialog extends HTMLElement {
    static open = new Set();

    static closeAll() {
      Array.from(DrawerDialog.open).forEach((d) => d.close());
    }

    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      // Escape is handled on the document: focus may sit on <body> after a re-render
      this.onKeyup = (event) => {
        if (event.code === 'Escape') this.close();
      };
      // start open when asked to (e.g. the password form came back with an error)
      if (this.hasAttribute('data-open-on-load') || this.querySelector('[data-open-drawer]')) {
        requestAnimationFrame(() => this.open(document.querySelector(`[data-drawer-open="${this.id}"]`)));
      }
    }

    disconnectedCallback() {
      if (this.classList.contains('is-open')) this.close();
    }

    focusTarget() {
      return this.querySelector('[data-drawer-focus]') || this.querySelector('[data-drawer-close]') || this;
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      const active = document.activeElement;
      this.opener = opener || (active && active !== document.body ? active : null);
      DrawerDialog.closeAll();
      this.classList.add('is-open');
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.setAttribute('aria-hidden', 'false');
      if (this.opener && this.opener.hasAttribute('aria-controls')) this.opener.setAttribute('aria-expanded', 'true');
      DrawerDialog.open.add(this);
      document.addEventListener('keyup', this.onKeyup);
      document.body.classList.add('overflow-hidden');
      Overlay.show();
      setTimeout(() => trapFocus.activate(this, this.focusTarget()), 50);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.removeAttribute('role');
      this.removeAttribute('aria-modal');
      this.removeAttribute('aria-hidden');
      DrawerDialog.open.delete(this);
      document.removeEventListener('keyup', this.onKeyup);
      if (DrawerDialog.open.size === 0) {
        document.body.classList.remove('overflow-hidden');
        Overlay.hide();
      }
      trapFocus.deactivate();
      if (this.opener) {
        if (this.opener.hasAttribute('aria-controls')) this.opener.setAttribute('aria-expanded', 'false');
        if (this.opener.isConnected && this.opener.focus) this.opener.focus();
      }
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  if (!customElements.get('drawer-dialog')) customElements.define('drawer-dialog', DrawerDialog);

  // Global triggers
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer && drawer.open) {
        event.preventDefault();
        drawer.open(opener);
      }
      return;
    }
    const cartToggle = event.target.closest('[data-cart-drawer-toggle]');
    if (cartToggle) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && drawer.open) {
        event.preventDefault();
        drawer.open(cartToggle);
      }
    }
  });

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends DrawerDialog {
    renderContents(parsedState, focusHint) {
      const sections = parsedState.sections || {};
      if (sections['cart-drawer']) {
        const doc = parseHTML(sections['cart-drawer']);
        const fresh = doc.querySelector('cart-drawer');
        if (fresh) {
          const hadFocus = this.contains(document.activeElement);
          this.className = fresh.className + (this.classList.contains('is-open') ? ' is-open' : '');
          this.innerHTML = fresh.innerHTML;
          if (this.classList.contains('is-open') && hadFocus) trapFocus.focus(CartItems.focusAfterUpdate(this, focusHint) || this.focusTarget());
        }
      }
      if (sections['cart-icon-bubble']) CartDrawer.renderBubble(sections['cart-icon-bubble']);
    }

    static renderBubble(html) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      const doc = parseHTML(html);
      const section = doc.querySelector('.shopify-section');
      bubble.innerHTML = section ? section.innerHTML : doc.body.innerHTML;
    }

    getSectionsToRender() {
      return ['cart-drawer', 'cart-icon-bubble'];
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* ---------- Header ---------- */
  class HeaderNav extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.header');
      if (!this.header) return;
      this.overlay = this.dataset.overlay === 'true';
      this.navHeight = this.header.offsetHeight || 56;
      this.themeSections = [];
      this.offset = 0;
      // an announcement bar above the header pushes the fixed bar down until it scrolls away
      const bar = document.querySelector('.announcement-bar');
      this.announcement = bar && bar.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING ? bar : null;
      if (this.announcement) document.documentElement.classList.add('has-announcement');
      this.setupMegaMenu();
      if (this.overlay) {
        this.collectThemeSections();
        // no hero to read a colour from: plain header
        if (!this.themeSections.length) this.dropOverlay();
      }
      this.onScroll = this.onScroll.bind(this);
      this.onResize = debounce(() => {
        this.navHeight = this.header.offsetHeight || 56;
        if (this.overlay) this.collectThemeSections();
        this.onScroll();
      }, 200);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    }

    dropOverlay() {
      this.overlay = false;
      this.removeAttribute('data-overlay');
      this.header.classList.remove('header--overlay', 'header--text-light', 'header--text-dark');
      if (this.dataset.border === 'true') this.header.classList.add('header--border');
    }

    collectThemeSections() {
      this.themeSections = Array.from(document.querySelectorAll('[data-nav-theme]'));
      this.ticking = false;
    }

    updateOffset() {
      const offset = this.announcement ? Math.max(0, Math.round(this.announcement.getBoundingClientRect().bottom)) : 0;
      if (offset === this.offset) return;
      this.offset = offset;
      document.documentElement.style.setProperty('--header-offset', `${offset}px`);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.updateOffset();
        if (this.overlay) {
          const probe = this.offset + this.navHeight / 2;
          let current = null;
          let currentTop = 0;
          for (const section of this.themeSections) {
            const rect = section.getBoundingClientRect();
            if (rect.top <= probe && rect.bottom >= probe) {
              current = section;
              currentTop = rect.top;
              break;
            }
          }
          if (current) {
            // clear over a hero at the top; frosted once the hero has scrolled past the bar's height
            this.header.classList.toggle('header--scrolled', currentTop < this.offset - this.navHeight);
            const theme = current.dataset.navTheme === 'dark' ? 'dark' : 'light';
            this.header.classList.toggle('header--text-light', theme === 'light');
            this.header.classList.toggle('header--text-dark', theme === 'dark');
          } else {
            this.header.classList.add('header--scrolled');
          }
        } else {
          this.header.classList.toggle('header--scrolled', window.scrollY > this.offset + 4);
        }
        this.ticking = false;
      });
    }

    setupMegaMenu() {
      this.items = Array.from(this.querySelectorAll('.header__nav-item--has-children'));
      if (!this.items.length) return;
      const syncHeaderState = () => this.header.classList.toggle('header--menu-open', this.items.some((i) => i.classList.contains('is-open')));
      const closeAll = (except) => {
        this.items.forEach((item) => {
          if (item !== except) {
            item.classList.remove('is-open');
            const btn = item.querySelector('[data-mega-toggle]');
            if (btn) btn.setAttribute('aria-expanded', 'false');
          }
        });
        syncHeaderState();
      };
      this.closeAllMenus = closeAll;
      this.items.forEach((item) => {
        const btn = item.querySelector('[data-mega-toggle]');
        let openTimer;
        let closeTimer;
        const open = () => {
          clearTimeout(closeTimer);
          closeAll(item);
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
          syncHeaderState();
        };
        const close = () => {
          item.classList.remove('is-open');
          btn.setAttribute('aria-expanded', 'false');
          syncHeaderState();
        };
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          item.classList.contains('is-open') ? close() : open();
        });
        if (window.matchMedia('(hover: hover)').matches) {
          item.addEventListener('mouseenter', () => {
            clearTimeout(closeTimer);
            openTimer = setTimeout(open, 60);
          });
          item.addEventListener('mouseleave', () => {
            clearTimeout(openTimer);
            closeTimer = setTimeout(close, 160);
          });
        }
        item.addEventListener('keyup', (event) => {
          if (event.code === 'Escape') {
            close();
            btn.focus();
          }
        });
        item.addEventListener('focusout', (event) => {
          // a click on non-focusable space in the panel moves focus to <body>: stay open
          if (event.relatedTarget === null && item.matches(':hover')) return;
          if (!item.contains(event.relatedTarget)) close();
        });
      });
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) closeAll();
      };
      document.addEventListener('click', this.onDocumentClick);
    }
  }
  if (!customElements.get('header-nav')) customElements.define('header-nav', HeaderNav);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });
      this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
      this.input.addEventListener('change', () => this.validate());
      this.validate();
    }

    onButtonClick(event) {
      event.preventDefault();
      const previous = this.input.value;
      event.currentTarget.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
      if (previous !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    validate() {
      const value = parseInt(this.input.value, 10);
      const min = parseInt(this.input.min, 10);
      const max = parseInt(this.input.max, 10);
      const minus = this.querySelector('[name="minus"]');
      const plus = this.querySelector('[name="plus"]');
      if (minus) minus.disabled = !Number.isNaN(min) && value <= min && min > 0;
      if (plus) plus.disabled = !Number.isNaN(max) && value >= max;
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Product form (AJAX add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.cart = document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorWrapper = this.querySelector('.product-form__error');
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
      this.setError('');
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('btn--loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().join(','));
        formData.append('sections_url', window.location.pathname);
      }
      config.body = formData;

      try {
        const response = await fetch(window.theme.routes.cartAdd, config);
        const data = await response.json();
        if (data.status) {
          this.setError(data.description || data.message || window.theme.strings.cartError);
          return;
        }
        if (!this.cart || window.theme.cartType === 'page') {
          window.location = window.theme.routes.cart;
          return;
        }
        this.cart.renderContents(data);
        this.cart.open(this.submitButton);
        announce(`${data.product_title || data.title} — ${window.theme.strings.addToCart}`);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (error) {
        this.setError(window.theme.strings.cartError);
      } finally {
        this.submitButton.classList.remove('btn--loading');
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    setError(message) {
      if (!this.errorWrapper) return;
      this.errorWrapper.textContent = message;
      this.errorWrapper.toggleAttribute('hidden', !message);
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- Cart items (drawer + page) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.sectionName = this.dataset.section || 'main-cart';
      this.timers = new Map();
      this.queue = Promise.resolve();
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) {
          event.preventDefault();
          this.enqueue(remove.dataset.index, 0, { name: 'remove', line: remove.dataset.index });
        }
      });
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches('.quantity__input')) return;
      const line = input.dataset.index;
      // debounced per line, sent one at a time: quick edits on two lines both apply, in order
      clearTimeout(this.timers.get(line));
      this.timers.set(
        line,
        setTimeout(() => this.enqueue(line, input.value, { name: 'input', line }, input.dataset.quantityVariantId), 300)
      );
    }

    enqueue(line, quantity, focusHint, variantId) {
      this.queue = this.queue.then(() => this.updateQuantity(line, quantity, focusHint, variantId));
    }

    getSectionsToRender() {
      const drawer = document.querySelector('cart-drawer');
      const list = [];
      if (this.sectionName === 'cart-drawer' && drawer) list.push(...drawer.getSectionsToRender());
      else list.push(this.dataset.sectionId || 'main-cart', 'cart-icon-bubble');
      return list;
    }

    // the control that was used is gone after a re-render: find its twin
    static focusAfterUpdate(root, hint) {
      if (!hint) return null;
      const item = root.querySelector(`.cart-item[data-index="${hint.line}"]`);
      if (item && hint.name === 'input') return item.querySelector('.quantity__input');
      if (item && hint.name === 'remove') return item.querySelector('[data-cart-remove]');
      return root.querySelector('.cart-item [data-cart-remove], .cart-item .quantity__input, [data-drawer-focus], [data-drawer-close]');
    }

    async updateQuantity(line, quantity, focusHint, variantId) {
      this.setLoading(true);
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender(),
        sections_url: window.location.pathname,
      });
      try {
        const response = await fetch(window.theme.routes.cartChange, { ...fetchConfig(), body });
        const state = await response.json();
        if (state.status || state.errors) {
          // /cart/change.js answers 422 with { status, message }
          this.showLineError(line, state.description || state.errors || window.theme.strings.cartError);
          this.resetLine(line);
          return;
        }
        const drawer = document.querySelector('cart-drawer');
        let root = this;
        if (this.sectionName === 'cart-drawer' && drawer) {
          drawer.renderContents(state, focusHint);
          root = drawer;
        } else {
          root = this.renderPage(state, focusHint) || this;
        }
        // Quantity rule check (item limited by stock)
        const updatedItem = state.items && state.items.find((item) => String(item.variant_id) === String(variantId));
        if (updatedItem && Number(quantity) > updatedItem.quantity && Number(quantity) !== 0) {
          CartItems.showLineErrorIn(root, line, window.theme.strings.quantityError.replace('[quantity]', updatedItem.quantity));
        }
        announce(Number(quantity) === 0 ? window.theme.strings.cartItemRemoved : window.theme.strings.cartUpdated);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: state }));
      } catch (error) {
        this.showLineError(line, window.theme.strings.cartError);
        this.resetLine(line);
      } finally {
        this.setLoading(false);
      }
    }

    renderPage(state, focusHint) {
      const sections = state.sections || {};
      const pageSection = sections[this.dataset.sectionId] || sections['main-cart'];
      let root = null;
      if (pageSection) {
        const doc = parseHTML(pageSection);
        const freshMain = doc.getElementById('MainCart');
        const currentMain = document.getElementById('MainCart');
        if (freshMain && currentMain) {
          const hadFocus = currentMain.contains(document.activeElement);
          currentMain.innerHTML = freshMain.innerHTML;
          root = currentMain;
          if (hadFocus) trapFocus.focus(CartItems.focusAfterUpdate(currentMain, focusHint) || currentMain.querySelector('a, button'));
        }
      }
      if (sections['cart-icon-bubble']) CartDrawer.renderBubble(sections['cart-icon-bubble']);
      return root;
    }

    resetLine(line) {
      const input = this.querySelector(`.cart-item[data-index="${line}"] .quantity__input`);
      if (input) input.value = input.getAttribute('value');
    }

    showLineError(line, message) {
      CartItems.showLineErrorIn(this, line, message);
    }

    static showLineErrorIn(root, line, message) {
      const item = root.querySelector(`.cart-item[data-index="${line}"]`);
      if (!item) return;
      const el = item.querySelector('.cart-item__error');
      if (el) el.textContent = message;
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

  class CartNote extends HTMLElement {
    connectedCallback() {
      this.addEventListener(
        'input',
        debounce((event) => {
          const field = event.target;
          fetch(window.theme.routes.cartUpdate, { ...fetchConfig(), body: JSON.stringify({ note: field.value }) })
            .then((response) => {
              if (!response.ok) throw new Error(response.status);
              field.setCustomValidity('');
            })
            .catch(() => {
              // the note still travels with the cart form; flag the failed save
              field.setCustomValidity(window.theme.strings.cartError);
              field.reportValidity();
            });
        }, 400)
      );
    }
  }
  if (!customElements.get('cart-note')) customElements.define('cart-note', CartNote);

  /* ---------- Localization ---------- */
  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('select').forEach((select) =>
        select.addEventListener('change', () => {
          const form = select.closest('form');
          if (form) form.submit();
        })
      );
    }
  }
  if (!customElements.get('localization-form')) customElements.define('localization-form', LocalizationForm);

  /* ---------- Video (hero / section) ---------- */
  class VideoSection extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-video-toggle]');
      if (!this.video) return;
      if (this.toggle) {
        const sync = () => {
          const playing = !this.video.paused && !this.video.ended;
          this.toggle.classList.toggle('is-playing', playing);
          this.toggle.setAttribute('aria-pressed', String(playing));
        };
        this.toggle.addEventListener('click', () => {
          if (this.video.paused) this.video.play().catch(() => {});
          else this.video.pause();
        });
        ['play', 'playing', 'pause', 'ended'].forEach((type) => this.video.addEventListener(type, sync));
        sync(); // autoplay may already be running before this attached
      }
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.video.removeAttribute('autoplay');
        this.video.pause();
      }
      if ('IntersectionObserver' in window && this.video.hasAttribute('autoplay')) {
        this.observer = new IntersectionObserver(
          (entries) => entries.forEach((e) => (e.isIntersecting ? this.video.play().catch(() => {}) : this.video.pause())),
          { threshold: 0.25 }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }
  if (!customElements.get('video-section')) customElements.define('video-section', VideoSection);

  // video poster: swap the cover for the embed and move focus into the player
  document.addEventListener('click', (event) => {
    const poster = event.target.closest('[data-video-poster]');
    if (!poster) return;
    const template = poster.parentElement.querySelector('[data-video-embed]');
    if (!template) return;
    const parent = poster.parentElement;
    poster.replaceWith(template.content.cloneNode(true));
    const frame = parent.querySelector('iframe');
    if (frame) {
      frame.setAttribute('tabindex', '0');
      frame.focus();
    }
  });

  /* ---------- Share button ---------- */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      if (!button) return;
      const url = this.dataset.url || window.location.href;
      const title = this.dataset.title || document.title;
      button.addEventListener('click', async () => {
        if (navigator.share) {
          try {
            await navigator.share({ url, title });
          } catch (e) {
            /* cancelled */
          }
        } else if (navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(url);
          } catch (e) {
            window.prompt(title, url); // clipboard blocked: let the shopper copy it
            return;
          }
          const message = this.querySelector('[data-share-message]');
          if (message) {
            message.textContent = window.theme.strings.shareSuccess;
            message.removeAttribute('hidden');
            setTimeout(() => message.setAttribute('hidden', ''), 2500);
          }
        }
      });
    }
  }
  if (!customElements.get('share-button')) customElements.define('share-button', ShareButton);

  /* ---------- Reveal on scroll ---------- */
  const setupReveal = () => {
    if (!document.body.classList.contains('reveal-enabled') || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    const observe = (root = document) => root.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => observer.observe(el));
    observe();
    window.theme.reveal = observe;
    document.addEventListener('shopify:section:load', (event) => observe(event.target));
  };

  /* ---------- Theme editor hooks ---------- */
  document.addEventListener('shopify:section:load', (event) => {
    const nav = event.target.querySelector('header-nav');
    if (nav && nav.collectThemeSections) nav.collectThemeSections();
    const anyNav = document.querySelector('header-nav');
    if (anyNav && anyNav.collectThemeSections) {
      anyNav.collectThemeSections();
      anyNav.onScroll && anyNav.onScroll();
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupReveal);
  else setupReveal();
})();
